/**
 * Карточка сцены на storyboard §66.
 *
 * Компактный медиа-блок: превью, тип, длительность, текст, статус, стоимость (§20).
 * Действия — в контекстном меню (§62), а не россыпью кнопок.
 */
import { useState } from 'react';
import { Copy, MoreHorizontal, Plus, RefreshCw, Trash2 } from 'lucide-react';
import type { SceneTraceRow, SceneView } from '@alterium/shared';
import { StatusDot } from '../ui/StatusDot';
import { useTranslation } from 'react-i18next';
import { noticeText } from '../../i18n/notice';

interface SceneCardProps {
  scene: SceneView;
  index: number;
  selected: boolean;
  onSelect: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onInsertAfter: () => void;
  onRegenerate: () => void;
  /**
   * Чем сделана сцена — сервисы и модели.
   *
   * Приходит только админу (§41: имена внешних сервисов не показываются
   * в пользовательской части). Для обычного пользователя поле не приходит
   * вовсе, а не приходит пустым.
   */
  trace?: SceneTraceRow;
}

export function SceneCard({
  scene,
  index,
  selected,
  onSelect,
  onDuplicate,
  onDelete,
  onInsertAfter,
  onRegenerate,
  trace,
}: SceneCardProps) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const graphics = scene.graphics as { kind?: string; value?: string; title?: string } | null;

  return (
    <div className="group relative">
      <button
        type="button"
        onClick={onSelect}
        className={
          selected
            ? 'flex w-full gap-4 rounded-card border border-accent bg-surface p-3 text-left'
            : 'flex w-full gap-4 rounded-card border border-line bg-surface p-3 text-left transition-colors duration-fast hover:border-line-strong'
        }
      >
        <div className="relative h-20 w-14 shrink-0 overflow-hidden rounded-control bg-surface-secondary">
          {scene.previewUrl ? (
            <img
              src={scene.previewUrl}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-[11px] text-content-muted">
              {index + 1}
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-meta text-content">
              {t(`enum.sceneType.${scene.type}`, { defaultValue: scene.typeLabel })}
            </span>
            <span className="text-meta text-content-muted">
              {t('scene.seconds', { value: scene.durationSec })}
            </span>
            <span className="ml-auto text-meta text-content-muted">
              {t('scene.unitsShort', { value: scene.estimatedUnits })}
            </span>
          </div>

          <p className="mt-1 line-clamp-2 text-body text-content-secondary">
            {graphics?.value || graphics?.title
              ? `${graphics.value ?? graphics.title}`
              : scene.text || scene.visualPrompt || t('scene.noDescription')}
          </p>

          {/* §68: три этапа, а не внутренние статусы */}
          {scene.stageLabel ? (
            <p className="mt-1.5 flex items-center gap-2 text-meta text-content-muted">
              <StatusDot tone="warn" />
              {scene.stageLabel}
            </p>
          ) : null}

          {scene.status === 'needs_input' && scene.missingRequirements.length > 0 ? (
            <p className="mt-1.5 flex items-center gap-2 text-meta text-warning">
              <StatusDot tone="warn" />
              {noticeText(scene.missingRequirements[0]!)}
            </p>
          ) : null}

          {scene.status === 'failed' ? (
            <p className="mt-1.5 flex items-center gap-2 text-meta text-error">
              <StatusDot tone="fail" />
              {t('scene.failed')}
            </p>
          ) : null}

          {/*
            Чем сделана сцена. Видит только админ — см. `trace`.

            Короткое имя модели, а не полный путь: «kling-video/v3/pro» вместо
            «fal-ai/kling-video/v3/pro/image-to-video». В карточке важно узнать
            модель с одного взгляда, а не прочитать её целиком.
          */}
          {trace && trace.steps.length > 0 ? (
            <p className="mt-1.5 font-mono text-[11px] leading-4 text-content-muted">
              {trace.steps
                .map((step) => shortModel(step.provider, step.model))
                .filter((value, index, all) => all.indexOf(value) === index)
                .join(' · ')}
            </p>
          ) : null}

          {scene.spentUnits > 0 && scene.status === 'ready' ? (
            <p className="mt-1.5 text-meta text-content-muted">
              {t('scene.spent', { value: scene.spentUnits })}
              {scene.variantCount > 1
                ? ` · ${t('scene.variants', { count: scene.variantCount })}`
                : ''}
            </p>
          ) : null}
        </div>
      </button>

      {/* §62: действия по hover / в контекстном меню */}
      <div className="absolute right-2 top-2 opacity-0 transition-opacity duration-fast group-hover:opacity-100">
        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-label={t('common.actions')}
          className="rounded-control bg-page/80 p-1.5 text-content-muted hover:text-content"
        >
          <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
        </button>

        {menuOpen ? (
          <div className="absolute right-0 top-9 z-10 flex w-48 flex-col rounded-card border border-line bg-surface py-1 shadow-lg">
            <MenuItem
              icon={<RefreshCw className="h-4 w-4" strokeWidth={1.75} />}
              onClick={() => {
                setMenuOpen(false);
                onRegenerate();
              }}
            >
              {scene.status === 'ready' ? t('scene.createVariant') : t('scene.regenerate')}
            </MenuItem>
            <MenuItem
              icon={<Copy className="h-4 w-4" strokeWidth={1.75} />}
              onClick={() => {
                setMenuOpen(false);
                onDuplicate();
              }}
            >
              {t('scene.duplicate')}
            </MenuItem>
            <MenuItem
              icon={<Plus className="h-4 w-4" strokeWidth={1.75} />}
              onClick={() => {
                setMenuOpen(false);
                onInsertAfter();
              }}
            >
              {t('scene.insertAfter')}
            </MenuItem>
            <MenuItem
              icon={<Trash2 className="h-4 w-4" strokeWidth={1.75} />}
              danger
              onClick={() => {
                setMenuOpen(false);
                onDelete();
              }}
            >
              {t('common.delete')}
            </MenuItem>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function MenuItem({
  icon,
  danger,
  onClick,
  children,
}: {
  icon: React.ReactNode;
  danger?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        danger
          ? 'flex items-center gap-2.5 px-3 py-2 text-left text-meta text-error transition-colors duration-fast hover:bg-surface-hover'
          : 'flex items-center gap-2.5 px-3 py-2 text-left text-meta text-content-secondary transition-colors duration-fast hover:bg-surface-hover hover:text-content'
      }
    >
      {icon}
      {children}
    </button>
  );
}

/**
 * Короткое имя модели для карточки.
 *
 * Из «fal-ai/kling-video/v3/pro/image-to-video» оставляем «kling-video/v3/pro»:
 * префикс поставщика и суффикс режима повторяются у всех строк и только мешают
 * сравнивать.
 */
function shortModel(provider: string, model: string | null): string {
  if (!model) return provider;

  return model
    .replace(/^[a-z0-9-]+\//i, '')
    .replace(/\/(image-to-video|text-to-video|image-to-image)$/i, '');
}
